import { FC } from 'react';
import { useTranslation } from 'react-i18next';

import { ArrowLeftOutline, ArrowRightOutline, Box, Button, HStack, useColorModeValue } from '@bonusly/design-system';

interface UserSpotlightNavigationProps {
	currentIndex: number;
	totalCards: number;
	onPrevious: () => void;
	onNext: () => void;
	onGoToCard: (index: number) => void;
}

export const UserSpotlightNavigation: FC<UserSpotlightNavigationProps> = ({ currentIndex, totalCards, onPrevious, onNext, onGoToCard }) => {
	const { t } = useTranslation('spotlight');
	const isFirst = currentIndex === 0;
	const isLast = currentIndex === totalCards - 1;
	// Force higher contrast back button in dark mode (spotlight background is bright)
	const backButtonColor = useColorModeValue('text.soft', 'gray.900');

	return (
		<Box
			position={{ base: 'fixed', md: 'relative' }}
			bottom={{ base: 0, md: 'auto' }}
			left={0}
			right={0}
			width="100%"
			maxW={{ base: '100%', md: '900px' }}
			mx="auto"
			mt={{ base: 0, md: 4 }}
			mb={{ base: 0, md: 2 }}
			pb={{ base: 'calc(env(safe-area-inset-bottom, 0px) + 24px)', md: 0 }}
			pt={{ base: 4, md: 0 }}
			bg={{ base: 'bg.surface', md: 'transparent' }}
			display="flex"
			justifyContent="space-between"
			alignItems="center"
			zIndex={50}
			flexShrink={0}
		>
			{/* Mobile: Just buttons with equal padding */}
			<Box display={{ base: 'flex', md: 'none' }} width="100%" justifyContent="space-between" px={6}>
				<Button
					leftIcon={<ArrowLeftOutline size="sm" />}
					variant="ghost"
					onClick={onPrevious}
					disabled={isFirst}
					px={4}
					py={2}
					color={backButtonColor}
					fontSize={{ base: 'xs', md: 'sm' }}
					_hover={{
						bg: 'none',
					}}
					_active={{
						bg: 'none',
					}}
				>
					{t('navigation.back')}
				</Button>

				<Button
					rightIcon={<ArrowRightOutline size="sm" />}
					variant="solid"
					onClick={onNext}
					visibility={isLast ? 'hidden' : 'visible'}
					bg="secondary.bg"
					color="secondary.fgOnSurface"
					px={4}
					py={2}
					h="auto"
					fontSize={{ base: 'xs', md: 'sm' }}
					_hover={{
						bg: 'secondary.bgHover',
						color: 'secondary.fgOnSurface',
					}}
					_active={{
						bg: 'secondary.bgActive',
						color: 'secondary.fgOnSurface',
					}}
				>
					{t('navigation.next')}
				</Button>
			</Box>

			{/* Desktop: Buttons with pills in middle */}
			<Box display={{ base: 'none', md: 'flex' }} width="100%" justifyContent="space-between" alignItems="center">
				<Button
					leftIcon={<ArrowLeftOutline size="sm" />}
					variant="ghost"
					onClick={onPrevious}
					disabled={isFirst}
					px="20px"
					py="10px"
					color={backButtonColor}
					_hover={{
						bg: 'none',
					}}
					_active={{
						bg: 'none',
					}}
				>
					{t('navigation.back')}
				</Button>

				<HStack spacing={2}>
					{Array.from({ length: totalCards }).map((_, index) => (
						<Box
							key={index}
							as="button"
							aria-label={t('navigation.goToCardAria', { number: index + 1 })}
							aria-current={index === currentIndex ? 'step' : undefined}
							w={index === currentIndex ? '1.5rem' : '0.75rem'}
							h="0.75rem"
							borderRadius="20px"
							bg={index === currentIndex ? 'text.default' : 'bg.surface'}
							cursor="pointer"
							transition="all 0.2s ease"
							onClick={() => onGoToCard(index)}
							_hover={{
								bg: index === currentIndex ? 'text.default' : 'bg.surface',
							}}
						/>
					))}
				</HStack>

				<Button
					rightIcon={<ArrowRightOutline size="sm" />}
					variant="solid"
					onClick={onNext}
					visibility={isLast ? 'hidden' : 'visible'}
					bg="secondary.bg"
					color="secondary.fgOnSurface"
					px="20px"
					py="10px"
					h="auto"
					fontSize="sm"
					_hover={{
						bg: 'secondary.bgHover',
						color: 'secondary.fgOnSurface',
					}}
					_active={{
						bg: 'secondary.bgActive',
						color: 'secondary.fgOnSurface',
					}}
				>
					{t('navigation.next')}
				</Button>
			</Box>
		</Box>
	);
};
